import type { ExpressionSpecification, FilterSpecification, LayerSpecification } from 'maplibre-gl'
import type { AirspaceType } from '@jupi/shared'
import { BASEMAP_FONT, BASEMAP_FONT_BOLD, type BasemapMode } from './basemap'

// The aeronautical overlay: airspace.geojson (db/generate/airspace) drawn
// in the usual VFR chart manner. Controlled airspace is lavender and violet,
// restrictions red, military areas a muted brown, and information zones
// and RMZs dashed with no fill. Fills stay faint so that three stacked
// areas still read, and the basemap's labels sit above them.
//
// One fill, two line (solid/dashed; MapLibre 4 cannot vary a dash per
// feature) and two label layers carry every type. The map's toggles only
// change their filter, so a theme switch or toggle never re-adds layers.

export const AIRSPACE_SOURCE = 'airspace'
export const AIRSPACE_URL = '/map/airspace.geojson'
export const AIRSPACE_FILL_LAYER = 'airspace-fill'

export const AIRSPACE_GROUPS = ['controlled', 'restricted', 'military', 'boundaries'] as const
export type AirspaceGroup = (typeof AIRSPACE_GROUPS)[number]

const GROUP_TYPES: Record<AirspaceGroup, AirspaceType[]> = {
  controlled: ['CTR', 'TMA', 'CTA', 'FIZ', 'RMZ', 'RAS'],
  restricted: ['P', 'R', 'D', 'ADIZ'],
  military: ['TRA', 'TSA', 'CBA'],
  boundaries: ['FIR', 'SECTOR'],
}

export interface AirspaceView {
  groups: AirspaceGroup[]
  /** VFR decluttering: hide everything whose lower limit is FL 95 or above. */
  belowFl95: boolean
}

export const DEFAULT_AIRSPACE_VIEW: AirspaceView = { groups: ['controlled', 'restricted'], belowFl95: true }

const DASHED: AirspaceType[] = ['FIZ', 'RMZ', 'RAS', 'D', 'ADIZ', 'TRA', 'TSA', 'CBA']
const NO_FILL: AirspaceType[] = ['FIZ', 'RMZ', 'RAS', 'ADIZ', 'FIR', 'SECTOR']

interface Tokens {
  controlledFill: string
  ctrFill: string
  controlledLine: string
  fizLine: string
  restrictedFill: string
  restrictedLine: string
  dangerLine: string
  militaryFill: string
  militaryLine: string
  boundaryLine: string
  label: string
  labelHalo: string
}

const TOKENS: Record<BasemapMode, Tokens> = {
  light: {
    controlledFill: '#9575cd',
    ctrFill: '#7e57c2',
    controlledLine: '#3f2a8c',
    fizLine: '#00796b',
    restrictedFill: '#e53935',
    restrictedLine: '#b71c1c',
    dangerLine: '#e65100',
    militaryFill: '#8d6e63',
    militaryLine: '#6d4c41',
    boundaryLine: '#8a8a94',
    label: '#3f2a8c',
    labelHalo: '#f3f3f1',
  },
  dark: {
    controlledFill: '#9575cd',
    ctrFill: '#9f7be0',
    controlledLine: '#b39ddb',
    fizLine: '#4db6ac',
    restrictedFill: '#ef5350',
    restrictedLine: '#ef9a9a',
    dangerLine: '#ffb74d',
    militaryFill: '#a1887f',
    militaryLine: '#bcaaa4',
    boundaryLine: '#6f7985',
    label: '#d1c4e9',
    labelHalo: '#1a1f24',
  },
}

const TYPE = ['get', 'type'] as ExpressionSpecification
const typeIn = (types: AirspaceType[]): ExpressionSpecification => ['in', TYPE, ['literal', types]]

/** The filter every airspace layer shares, from the toggles. */
export function airspaceFilter(view: AirspaceView): ExpressionSpecification {
  const types = view.groups.flatMap((g) => GROUP_TYPES[g])
  const visible: ExpressionSpecification = typeIn(types)
  if (!view.belowFl95) return visible
  // FIR and ACC sectors reach the surface on paper but are only lines;
  // "below FL 95" is about areas a VFR flight could enter.
  return ['all', visible, ['<', ['coalesce', ['get', 'lowerFt'], 0], 9500]]
}

function withView(view: AirspaceView, own: ExpressionSpecification | null): FilterSpecification {
  return own ? ['all', airspaceFilter(view), own] : airspaceFilter(view)
}

/** Layers in draw order. The caller inserts them beneath the basemap's labels. */
export function airspaceLayers(mode: BasemapMode, view: AirspaceView): LayerSpecification[] {
  const c = TOKENS[mode]
  const lineColor: ExpressionSpecification = [
    'match',
    TYPE,
    ['CTR', 'TMA', 'CTA', 'RMZ', 'RAS'],
    c.controlledLine,
    'FIZ',
    c.fizLine,
    ['P', 'R', 'ADIZ'],
    c.restrictedLine,
    'D',
    c.dangerLine,
    ['TRA', 'TSA', 'CBA'],
    c.militaryLine,
    c.boundaryLine,
  ]
  const lineWidth: ExpressionSpecification = [
    'interpolate',
    ['linear'],
    ['zoom'],
    5,
    ['match', TYPE, ['CTR', 'P', 'FIR'], 1.2, 0.7],
    10,
    ['match', TYPE, ['CTR', 'P', 'FIR'], 2.2, 1.4],
  ]
  return [
    {
      // Every type has a fill, transparent where it should not show, so a
      // tap anywhere inside an area finds it (the popup queries this layer).
      id: AIRSPACE_FILL_LAYER,
      type: 'fill',
      source: AIRSPACE_SOURCE,
      filter: withView(view, null),
      paint: {
        'fill-color': [
          'match',
          TYPE,
          'CTR',
          c.ctrFill,
          ['TMA', 'CTA'],
          c.controlledFill,
          ['P', 'R'],
          c.restrictedFill,
          'D',
          c.dangerLine,
          ['TRA', 'TSA', 'CBA'],
          c.militaryFill,
          c.controlledFill,
        ],
        'fill-opacity': [
          'case',
          typeIn(NO_FILL),
          0,
          // Danger areas are many and overlap; they stay the faintest fill.
          ['match', TYPE, ['CTR', 'P'], 0.22, 'R', 0.16, 'D', 0.07, ['TRA', 'TSA', 'CBA'], 0.07, 0.12],
        ],
      },
    },
    {
      id: 'airspace-line',
      type: 'line',
      source: AIRSPACE_SOURCE,
      filter: withView(view, ['!', typeIn(DASHED)]),
      paint: { 'line-color': lineColor, 'line-width': lineWidth },
    },
    {
      id: 'airspace-line-dashed',
      type: 'line',
      source: AIRSPACE_SOURCE,
      filter: withView(view, typeIn(DASHED)),
      paint: { 'line-color': lineColor, 'line-width': lineWidth, 'line-dasharray': [3, 2] },
    },
    {
      // ACC sectors are frequency boundaries: labelled along their edge.
      id: 'airspace-sector-label',
      type: 'symbol',
      source: AIRSPACE_SOURCE,
      minzoom: 6,
      filter: withView(view, ['==', TYPE, 'SECTOR']),
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 400,
        'text-field': ['concat', ['get', 'name'], ' ', ['coalesce', ['get', 'freq'], '']],
        'text-font': BASEMAP_FONT,
        'text-size': 10,
        'text-offset': [0, -0.7],
      },
      paint: { 'text-color': c.boundaryLine, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.2 },
    },
    {
      // Name over "upper / lower", the way a VFR chart prints an area.
      id: 'airspace-label',
      type: 'symbol',
      source: AIRSPACE_SOURCE,
      minzoom: 7,
      filter: withView(view, ['!', typeIn(['FIR', 'SECTOR', 'ADIZ'])]),
      layout: {
        'text-field': [
          'format',
          ['get', 'name'],
          { 'text-font': ['literal', BASEMAP_FONT_BOLD] },
          '\n',
          {},
          ['concat', ['get', 'upperText'], ' / ', ['get', 'lowerText']],
          { 'font-scale': 0.85 },
        ],
        'text-font': BASEMAP_FONT,
        'text-size': 10,
        'text-max-width': 12,
        'symbol-sort-key': ['coalesce', ['get', 'lowerFt'], 0],
      },
      paint: { 'text-color': c.label, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.4 },
    },
  ]
}

/** Every airspace layer's id, for filter updates and restyles. */
export const AIRSPACE_LAYER_IDS = airspaceLayers('light', DEFAULT_AIRSPACE_VIEW).map((l) => l.id)
